import axios from 'axios';
import environment from '../config/environment';

class S3AnalyticsService {
  constructor() {
    this.env = environment();
    
    // Set the base URL for API calls - use relative URL to avoid CORS issues
    this.baseUrl = '/api';
    
    // Analytics endpoints
    this.endpoints = {
      buckets: `${this.baseUrl}/analytics/s3/buckets`,
      metrics: `${this.baseUrl}/analytics/s3/metrics`,
      objects: `${this.baseUrl}/analytics/s3/objects`,
      permissions: `${this.baseUrl}/analytics/s3/permissions`,
      connection: `${this.baseUrl}/analytics/s3/connection`
    };
    
    // Connection state
    this.connectionState = {
      status: 'disconnected',
      lastChecked: null,
      error: null
    };
    
    // Debug mode
    this.debug = true;
  }

  log(...args) {
    if (this.debug) {
      console.log('[S3AnalyticsService]', ...args);
    }
  }

  logError(...args) {
    console.error('[S3AnalyticsService]', ...args);
  }

  async checkConnection() {
    try {
      this.log('Checking S3 connection...');
      
      const response = await axios.get(this.endpoints.connection, { 
        timeout: 10000,
        validateStatus: null  // Allow all status codes for better error handling
      });
      
      this.log('Raw response:', response);
      
      // Check if the request was successful
      if (response.status !== 200) {
        throw new Error(`Server returned status ${response.status}: ${response.data?.error || 'Unknown error'}`);
      }

      if (response.data && response.data.status === 'connected') {
        this.connectionState = {
          status: 'connected',
          lastChecked: new Date(),
          error: null,
          diagnostics: response.data.diagnostics
        };
      } else {
        this.connectionState = {
          status: 'disconnected',
          lastChecked: new Date(),
          error: response.data.error || 'Invalid connection response',
          diagnostics: response.data.diagnostics
        };
      }
      
      this.log('Connection state updated:', this.connectionState);
      return this.connectionState;
      
    } catch (error) {
      this.logError('Connection error:', error);
      
      this.connectionState = {
        status: 'disconnected',
        lastChecked: new Date(),
        error: error.response?.data?.error || error.message,
        details: {
          status: error.response?.status,
          data: error.response?.data,
          message: error.message
        }
      };
      
      return this.connectionState;
    }
  }

  async getS3Analytics() {
    try {
      this.log('Fetching S3 analytics data...');
      
      // First check connection
      const connectionStatus = await this.checkConnection();
      
      if (connectionStatus.status !== 'connected') {
        return {
          connectionState: connectionStatus,
          buckets: [],
          totalBuckets: 0,
          totalSize: '0 B',
          totalSizeBytes: 0,
          totalObjects: 0,
          timestamp: new Date().toISOString()
        };
      }
      
      // Get all metrics in parallel
      const [buckets, metrics] = await Promise.all([
        this.getBuckets(),
        this.getMetrics()
      ]);

      this.log('S3 data received');
      
      // Calculate totals
      const totalSize = this.calculateTotalSize(buckets);
      const totalObjects = this.calculateTotalObjects(buckets);
      
      return {
        connectionState: this.connectionState,
        buckets,
        totalBuckets: buckets.length,
        totalSize: this.formatBytes(totalSize),
        totalSizeBytes: totalSize,
        totalObjects,
        metrics,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      this.logError('Error fetching S3 analytics:', error);
      return {
        connectionState: this.connectionState,
        error: error.message,
        buckets: [],
        totalBuckets: 0,
        totalSize: '0 B',
        totalSizeBytes: 0,
        totalObjects: 0
      };
    }
  }

  async getBuckets() {
    try {
      const response = await axios.get(this.endpoints.buckets);
      
      // Format the data
      return response.data.map(bucket => ({
        ...bucket,
        sizeFormatted: this.formatBytes(bucket.sizeBytes || 0)
      }));
    } catch (error) {
      this.logError('Error fetching S3 buckets:', error);
      throw error;
    }
  }

  async getMetrics() {
    try {
      const response = await axios.get(this.endpoints.metrics);
      return response.data;
    } catch (error) {
      this.logError('Error fetching S3 metrics:', error);
      return {
        error: error.message
      };
    }
  }

  async getBucketObjects(bucketName) {
    try {
      this.log(`Fetching objects for bucket: ${bucketName}`);
      const response = await axios.get(`${this.endpoints.objects}/${bucketName}`);
      return response.data;
    } catch (error) {
      this.logError(`Error fetching objects for bucket ${bucketName}:`, error);
      return {
        error: error.message,
        objects: []
      };
    }
  }

  async checkBucketPermissions(bucketName) {
    try {
      this.log(`Checking permissions for bucket: ${bucketName}`);
      const response = await axios.get(`${this.endpoints.permissions}/${bucketName}`);
      return response.data;
    } catch (error) {
      this.logError(`Error checking permissions for bucket ${bucketName}:`, error);
      return {
        error: error.message,
        permissions: {}
      };
    }
  }

  calculateTotalSize(buckets) {
    return buckets.reduce((total, bucket) => total + (bucket.sizeBytes || 0), 0);
  }

  calculateTotalObjects(buckets) {
    return buckets.reduce((total, bucket) => total + (bucket.objectCount || 0), 0);
  }

  formatBytes(bytes, decimals = 2) {
    if (bytes === 0 || !bytes) return '0 B';
    
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
  }
}

// Create and export singleton instance
const s3AnalyticsService = new S3AnalyticsService();
export default s3AnalyticsService; 